import type React from 'react';
import {useState} from 'react';
import {Box, Divider, IconButton, ListItemIcon, MenuItem, Stack, TextField, Typography} from '@mui/material';
import {Add, Close} from '@mui/icons-material';
import type {RecordActions} from '../../hooks/useRecordActions';
import type {CategoryRecord} from '../../types/categoryRecordTypes';
import {ProfileSection} from '../components/ProfileSection';
import {PersonDialog} from './PersonDialog';
import {CategorySpecification} from './CategorySpecification';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';

const ADD = '__add__';
const NONE = '';

type Props = {
    record: CategoryRecord;
    actions: RecordActions;
    /** The category's taxonomy definition, the specification until somebody writes one. */
    taxonomyDescription: string;
};

/** General information (spec 4.4): the specification and the category owner, half the width each. Every change offers Undo. */
export const GeneralInformationSection: React.FC<Props> = ({record, actions, taxonomyDescription}) => {
    const [adding, setAdding] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const canEdit = record.canEdit;
    const owner = record.team.find(m => m.id === record.ownerId);

    const onOwnerChange = (value: string) => {
        if (value === ADD) {
            setAdding(true);
            return;
        }
        void actions.setOwner(value === NONE ? null : Number(value)).catch(() => undefined);
    };

    return (
        <ProfileSection title="General information" help={OVERVIEW_HELP.general}
                        subtitle={record.updatedBy ? `Last changed by ${record.updatedBy}` : undefined}>
            <Stack direction={{xs: 'column', md: 'row'}} gap={{xs: 2.5, md: 4}} alignItems="flex-start">
                <Box sx={{flex: 1, minWidth: 0, width: '100%'}}>
                    <CategorySpecification specification={record.specification} taxonomyDescription={taxonomyDescription}
                                           canEdit={canEdit} onSave={actions.setSpecification}/>
                </Box>
                <Box sx={{flex: 1, minWidth: 0, width: '100%', pt: {md: 3.75}}}>
                    {canEdit ? (
                        <TextField
                            select fullWidth size="small" label="Category owner"
                            value={owner ? String(owner.id) : NONE}
                            onChange={e => onOwnerChange(e.target.value)}
                            slotProps={{select: {
                                open: menuOpen, onOpen: () => setMenuOpen(true), onClose: () => setMenuOpen(false),
                                displayEmpty: true,
                                renderValue: () => owner ? `${owner.name} · ${owner.role}` : <em>No owner</em>,
                            }, inputLabel: {shrink: true}}}
                        >
                            <MenuItem value={NONE}><em>No owner</em></MenuItem>
                            {/* Only app users can own a category (the current owner stays listed). */}
                            {record.team.filter(m => m.userId !== null || m.id === record.ownerId).map(m => (
                                <MenuItem key={m.id} value={String(m.id)} sx={{justifyContent: 'space-between', gap: 1}}>
                                    <span>{m.name} <Typography component="span" variant="caption" color="text.secondary">{m.role}</Typography></span>
                                    <IconButton size="small" aria-label={`Remove ${m.name} from the team`}
                                                onMouseDown={e => e.stopPropagation()}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setMenuOpen(false);
                                                    void actions.removeTeamMember(m).catch(() => undefined);
                                                }}>
                                        <Close fontSize="inherit"/>
                                    </IconButton>
                                </MenuItem>
                            ))}
                            <Divider/>
                            <MenuItem value={ADD}>
                                <ListItemIcon><Add fontSize="small"/></ListItemIcon>
                                Add new category owner
                            </MenuItem>
                        </TextField>
                    ) : (
                        <>
                            <Typography variant="overline" color="text.secondary">Category owner</Typography>
                            <Typography variant="body2">{owner ? `${owner.name} · ${owner.role}` : 'No owner yet'}</Typography>
                        </>
                    )}
                </Box>
            </Stack>

            {adding && (
                <PersonDialog
                    title="Add new category owner" submitLabel="Add owner"
                    roles={record.options.roles} raci={record.options.raci} defaultRaci="Accountable" defaultRole="Category manager"
                    onSubmit={(input, name) => actions.addTeamMember(input, name, true)}
                    onClose={() => setAdding(false)}
                />
            )}
        </ProfileSection>
    );
};
